import { mountAdventure, getActiveAdventure, type AdventureContext } from "./ActiveAdventure.js";
import { loadAdventureLootTables } from "../inventory/loadLootTables.js";

/**
 * Adventure-aware boot helper (P19).
 *
 * Pulls the "mount manifest + load loot tables" sequence out of `main.tsx`
 * module-load so the launch gate (P20) can call it once the player has
 * chosen an adventure. No scene construction or `LOAD_MAP` dispatch
 * happens here — the caller owns those steps because the post-boot
 * sequence differs between fresh-game, reuse-party, and load-save paths.
 *
 * Errors on either sub-step are logged and swallowed to match the pre-P19
 * behaviour: manifests and loot tables are best-effort, the dev server
 * still has to boot into *something* if assets are missing.
 */
export async function bootAdventure(manifestId: string): Promise<AdventureContext> {
  const baseUrl = manifestIdToBaseUrl(manifestId);
  try {
    await mountAdventure(baseUrl);
  } catch (err) {
    console.warn(
      `[boot] Adventure manifest fetch failed, falling back to default context:`,
      err,
    );
  }
  try {
    await loadAdventureLootTables();
  } catch (err) {
    console.warn("[boot] loot_tables.json fetch failed:", err);
  }
  return getActiveAdventure();
}

/**
 * Translate a manifest id (e.g. `"bkcrawl-mvp"`) to its asset base URL.
 * Accepts a bare id or an already-resolved path for flexibility — the
 * launch picker hands us ids from `/engine/adventures.json`, but tests
 * and dev harnesses sometimes pass a full path.
 */
export function manifestIdToBaseUrl(manifestIdOrPath: string): string {
  if (manifestIdOrPath.startsWith("/") || manifestIdOrPath.startsWith("http")) {
    return manifestIdOrPath.replace(/\/+$/, "");
  }
  return `/${manifestIdOrPath}`;
}
